// Tenantra Inc. - Admin: Property ownership review queue (Didit Proof-of-Address results)
import { useEffect, useState } from 'react';
import { Loader2, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { formatDate } from '@/utils/date';

const FILTERS = [
  { key: 'manual_review', label: 'Needs decision' },
  { key: 'needs_documents', label: 'Awaiting re-upload' },
  { key: 'pending', label: 'No document yet' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'verified', label: 'Verified' },
];

const OUTCOME_STYLE = {
  verified: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  manual_review: 'text-amber-700 bg-amber-50 border-amber-200',
  needs_documents: 'text-slate-700 bg-slate-100 border-slate-200',
  rejected: 'text-red-700 bg-red-50 border-red-200',
  pending: 'text-slate-500 bg-white border-slate-200',
};

const STATUS_LABEL = { verified: 'Verified', manual_review: 'Needs decision', needs_documents: 'Re-upload requested', rejected: 'Rejected', pending: 'No document' };

const humanize = (s) => (s || '').toLowerCase().replace(/_/g, ' ');

export const PropertyReviewPanel = () => {
  const [filter, setFilter] = useState('manual_review');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/verifications/properties?status=${filter}&limit=100`);
      setItems(r.data.items);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  return (
    <div data-testid="admin-property-review-panel">
      <div className="mb-4">
        <h1 className="text-lg font-bold text-slate-900">Ownership Review</h1>
        <p className="text-slate-500 text-[11px]">Documents flagged by the automated check. Clean passes are verified automatically.</p>
      </div>

      <div className="flex border border-slate-200 mb-4 w-fit" data-testid="property-review-filters">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`px-3 h-8 text-xs ${filter === f.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`property-review-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="property-review-table">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Property</th>
              <th className="text-left px-3 py-2 font-medium">Landlord</th>
              <th className="text-left px-3 py-2 font-medium">Document</th>
              <th className="text-left px-3 py-2 font-medium">Flags</th>
              <th className="text-left px-3 py-2 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500" data-testid="property-review-empty">Nothing in this queue</td></tr>
            ) : items.map(p => (
              <tr key={p.id} onClick={() => setSelected(p)} className="hover:bg-slate-50 cursor-pointer" data-testid={`property-review-row-${p.id}`}>
                <td className="px-3 py-2 font-medium text-slate-900">{p.address}</td>
                <td className="px-3 py-2">
                  <p className="text-slate-900">{p.landlord_name}</p>
                  <p className="text-slate-500">{p.landlord_email}</p>
                </td>
                <td className="px-3 py-2 text-slate-700 capitalize">{p.ownership_review ? humanize(p.ownership_review.document_subtype || p.ownership_review.document_type) : 'None uploaded'}</td>
                <td className="px-3 py-2"><FlagSummary review={p.ownership_review} status={p.verification_status} /></td>
                <td className="px-3 py-2 text-slate-500">{formatDate(p.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <ReviewDrawer item={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} />}
    </div>
  );
};

const FlagSummary = ({ review, status }) => {
  if (!review) return <span className="text-slate-400">—</span>;
  const flags = (review.warnings || []).length;
  const tampered = review.forensics?.is_tampered || review.forensics?.overlay_detected || review.forensics?.image_anomalies_detected;
  return (
    <span className={`inline-block border px-1.5 py-0.5 text-[10px] ${OUTCOME_STYLE[status] || OUTCOME_STYLE.pending}`}>
      {tampered ? 'Tamper signals' : flags ? `${flags} warning${flags > 1 ? 's' : ''}` : 'Clean'}
    </span>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100 last:border-0">
    <dt className="text-[10px] uppercase tracking-wide text-slate-500 pt-0.5">{label}</dt>
    <dd className={`col-span-2 text-xs text-slate-900 ${mono ? 'font-mono' : ''}`}>{value ?? <span className="text-slate-400">Not detected</span>}</dd>
  </div>
);

const ReviewDrawer = ({ item, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null);
  const r = item.ownership_review;
  const f = r?.forensics;
  const decidable = ['manual_review', 'pending', 'needs_documents', 'rejected'].includes(item.verification_status);

  const openDocument = async () => {
    try {
      const res = await api.get(`/files/${item.verification_document_id}`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    }
  };

  const decide = async (approve) => {
    if (!approve && !reason.trim()) { toast.error('Enter a reason the landlord will see'); return; }
    setBusy(approve ? 'approve' : 'reject');
    try {
      await api.post(`/admin/verifications/properties/${item.id}/${approve ? 'approve' : 'reject'}`, approve ? { notes: reason || null } : { reason });
      toast.success(approve ? 'Property verified' : 'Document rejected');
      onDone();
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setBusy(null);
    }
  };

  const tamperSignals = f ? [
    f.is_tampered && 'Modified after digital signing',
    f.overlay_detected && `Overlay text manipulation (${(f.overlay_signals || []).map(humanize).join(', ') || 'detected'})`,
    f.image_anomalies_detected && 'Image anomalies detected',
    f.processed_by_known_editor && `Processed by editor: ${f.processed_by_known_editor === true ? 'yes' : f.processed_by_known_editor}`,
    f.has_different_creation_mod_date && 'Creation and modification dates differ',
  ].filter(Boolean) : [];

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="property-review-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">{STATUS_LABEL[item.verification_status] || item.verification_status}</p>
            <h2 className="text-sm font-semibold text-slate-900">{item.address}</h2>
            <p className="text-xs text-slate-500">{item.landlord_name} · {item.landlord_email} · ID {humanize(item.landlord_id_status) || 'unknown'}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100" aria-label="Close" data-testid="property-review-close"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {!r ? (
            <p className="text-xs text-slate-500" data-testid="property-review-no-document">No ownership document has been uploaded for this property.</p>
          ) : (
            <>
              <section>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-[10px] uppercase tracking-wide text-slate-500">Automated check</h3>
                  <button onClick={openDocument} className="text-xs text-teal-700 hover:underline inline-flex items-center gap-1" data-testid="property-review-open-document">
                    Open document <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
                <dl>
                  <Row label="Verdict" value={<span className={`inline-block border px-1.5 py-0.5 text-[10px] ${OUTCOME_STYLE[r.outcome]}`}>{r.provider_status} → {STATUS_LABEL[r.outcome] || r.outcome}</span>} />
                  <Row label="Document" value={<span className="capitalize">{humanize(r.document_subtype || r.document_type)}</span>} />
                  <Row label="Issuer" value={r.issuer} />
                  <Row label="Issue date" value={r.issue_date} />
                  <Row label="File" value={r.original_filename} />
                  <Row label="Checked" value={formatDate(r.checked_at, true)} />
                </dl>
              </section>

              <section>
                <h3 className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Name and address match</h3>
                <dl>
                  <Row label="Name on document" value={r.name_on_document} />
                  <Row label="Expected (KYC)" value={r.expected_name} />
                  <Row label="Name score" value={r.name_match_score != null ? `${Math.round(r.name_match_score)} / 100` : null} />
                  <Row label="Address on document" value={r.extracted_address} />
                  <Row label="Expected (property)" value={r.expected_address} />
                </dl>
              </section>

              <section>
                <h3 className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Warnings</h3>
                {(r.warnings || []).length === 0 ? (
                  <p className="text-xs text-emerald-700" data-testid="property-review-no-warnings">No warnings raised</p>
                ) : (
                  <ul className="space-y-1" data-testid="property-review-warnings">
                    {r.warnings.map((w, i) => (
                      <li key={i} className="text-xs border border-slate-200 px-2 py-1.5">
                        <span className={`font-mono text-[10px] mr-2 ${w.severity === 'error' ? 'text-red-700' : 'text-amber-700'}`}>{w.risk}</span>
                        <span className="text-slate-700">{w.description}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">File forensics</h3>
                {!f ? <p className="text-xs text-slate-500">Not available for this file</p> : (
                  <dl>
                    <Row label="Tamper signals" value={tamperSignals.length ? <ul className="text-red-700 space-y-0.5">{tamperSignals.map((t, i) => <li key={i}>{t}</li>)}</ul> : <span className="text-emerald-700">None</span>} />
                    <Row label="Producer" value={f.producer} mono />
                    <Row label="Creator" value={f.creator} mono />
                    <Row label="Created" value={f.creation_date} mono />
                    <Row label="Modified" value={f.modified_date} mono />
                    <Row label="Digitally signed" value={f.is_signed == null ? null : f.is_signed ? 'Yes' : 'No'} />
                  </dl>
                )}
              </section>
            </>
          )}

          {item.rejection_reason && <p className="text-xs text-red-700 border border-red-200 bg-red-50 px-2 py-1.5" data-testid="property-review-prior-rejection">Previously rejected: {item.rejection_reason}</p>}

          {decidable && (
            <section className="border-t border-slate-200 pt-4">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-1">Reason or note (shown to landlord on rejection)</label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="text-xs rounded-none border-slate-300" placeholder="e.g. Name on tax bill does not match verified identity" data-testid="property-review-reason" />
              <div className="flex gap-2 mt-3">
                <Button onClick={() => decide(true)} disabled={!!busy} className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white rounded-none" data-testid="property-review-approve-btn">
                  {busy === 'approve' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Approve ownership'}
                </Button>
                <Button onClick={() => decide(false)} disabled={!!busy} variant="outline" className="h-8 text-xs rounded-none border-slate-300 text-red-700 hover:bg-red-50" data-testid="property-review-reject-btn">
                  {busy === 'reject' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Reject'}
                </Button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
